import { useMemo } from 'react'
import { MdrModule } from '@/core/modules/mdr/mdr.module'
import { useMdrCasesQuery } from '@core/hooks/useMdrCasesQuery'
import { useCustomTheme } from '@/core/theme'
import {
    getBucketBoundariesAndLabels,
    TOTAL_BUCKETS,
} from './useAlertsOverTime.utils'
import { TTheme } from '@/core/theme'

export function useAlertsOverTime(
    timeRange: { id: string; label: string },
    chartRef?: React.RefObject<any>
) {
    const theme = useCustomTheme() as TTheme
    const { data: casesData } = useMdrCasesQuery()
    const alerts = casesData
        ? MdrModule.processCases(casesData, timeRange)['alerts']
        : []

    const { boundaries: bucketDates, labels: months } = useMemo(
        () => getBucketBoundariesAndLabels(timeRange, alerts),
        [alerts, timeRange]
    )

    const alertCounts = useMemo(() => {
        if (bucketDates.length < 2) return []
        return bucketDates.slice(0, -1).map((start, i) => {
            const end = bucketDates[i + 1]
            return alerts.filter((alert: any) => {
                const alertDate = new Date(alert.createdAt)
                if (i === bucketDates.length - 2) {
                    return alertDate >= start && alertDate <= end
                }
                return alertDate >= start && alertDate < end
            }).length
        })
    }, [alerts, bucketDates])

    const getGradient = (
        ctx: CanvasRenderingContext2D,
        area: { top: number; bottom: number }
    ) => {
        const gradient = ctx.createLinearGradient(0, area.top, 0, area.bottom)
        gradient.addColorStop(
            0,
            theme.colors.semantic.alertsOverTimeGradientTop
        )
        gradient.addColorStop(
            1,
            theme.colors.semantic.alertsOverTimeGradientBottom
        )
        return gradient
    }

    const data = useMemo(
        () => ({
            labels: months,
            datasets: [
                {
                    label: 'Alerts Generated',
                    data: alertCounts,
                    borderColor: theme.colors.primary[300],
                    backgroundColor: (context: any) => {
                        const chart =
                            chartRef?.current?.chartInstance ||
                            chartRef?.current?.ctx?.canvas
                                ? chartRef.current
                                : context.chart
                        const { ctx, chartArea } = chart
                        if (!chartArea) return 'rgba(59, 130, 246, 0.1)'
                        return getGradient(ctx, chartArea)
                    },
                    tension: 0,
                    fill: true,
                    pointRadius: (ctx: any) => (ctx.raw > 0 ? 2 : 0),
                    pointBackgroundColor: theme.colors.primary[300],
                    pointHoverRadius: (ctx: any) => (ctx.raw > 0 ? 4 : 0),
                    borderWidth: 1.5,
                    datalabels: {
                        align: (ctx: any) => {
                            const data = ctx.dataset.data as number[]
                            const value = data[ctx.dataIndex]
                            const max = Math.max(...data)
                            const yScale = ctx.chart.scales.y
                            const yPixel = yScale.getPixelForValue(value)
                            const chartBottom = ctx.chart.chartArea.bottom
                            const chartTop = ctx.chart.chartArea.top
                            if (yPixel >= chartBottom - 20)
                                return 'top' as const
                            if (yPixel <= chartTop + 20)
                                return 'bottom' as const
                            if (value <= 1) return 'top' as const
                            if (ctx.dataIndex === data.length - 1)
                                return 'top' as const
                            if (value === max && value > 1)
                                return 'bottom' as const
                            return 'top' as const
                        },
                        anchor: () => 'end' as const,
                        backgroundColor: theme.colors.primary[300],
                        borderRadius: 8,
                        color: theme.colors.greys[0],
                        font: { weight: 'bold' as const, size: 14 },
                        padding: { top: 4, bottom: 4, left: 8, right: 8 },
                        display: (context: any) =>
                            context.dataset.data[context.dataIndex] > 0,
                        formatter: (value: number) => value,
                        offset: 6,
                    },
                },
            ],
        }),
        [months, alertCounts, theme, chartRef]
    )

    const maxValue = Math.max(...alertCounts)
    const yAxisMax = Math.ceil(maxValue / 100) * 100 || 100
    const yAxisStep = Math.ceil(yAxisMax / 2)

    const options = useMemo(() => {
        const gridColor = theme.colors.greys[200]
        return {
            responsive: true,
            animation: {
                duration: 1000,
                easing: 'easeOutQuart' as const,
            },
            plugins: {
                legend: { display: false },
                title: { display: false },
                tooltip: { enabled: false },
                datalabels: { display: true },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    max: yAxisMax,
                    ticks: {
                        count: 3,
                        callback: function (
                            this: any,
                            tickValue: number | string
                        ) {
                            if (tickValue === 0) return ''
                            return typeof tickValue === 'number'
                                ? tickValue.toLocaleString()
                                : tickValue
                        },
                        color: theme.colors.greys[500],
                        font: { size: 12 },
                    },
                    grid: {
                        color: gridColor,
                        drawTicks: true,
                        drawBorder: true,
                    },
                },
                x: {
                    grid: { display: true, color: gridColor },
                    ticks: {
                        count: TOTAL_BUCKETS,
                        callback: (_: any, index: number) => months[index],
                        maxRotation: 0,
                        color: theme.colors.greys[500],
                        font: { size: 12 },
                    },
                },
            },
        }
    }, [months, yAxisMax, yAxisStep, theme])

    return { data, options }
}
